import { Eye, GripVertical, Plus, Trash2, Upload } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { useChatbot } from '../../contexts/ChatbotContext';
import { useMiniAppModal } from '../../hooks/useMiniAppModal';
import { useMiniApps } from '../../hooks/useMiniApps';
import { supabase } from '../../lib/supabase';

interface Service {
  id: string;
  title: string;
  subtitle: string | null;
  content: string;
  image_url: string | null;
  button_text?: string | null;
  button_url?: string | null;
  position: number | null;
  chatbot_public_id: string;
  created_at?: string;
  updated_at?: string;
}

export default function ServicesSettings() {
  const { chatbotPublicId, isLoading: isChatbotLoading } = useChatbot();
  const { openModal } = useMiniAppModal();
  const { miniApps, updateMiniAppLabel } = useMiniApps();
  const [services, setServices] = useState<Service[]>([]);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [uploadingImage, setUploadingImage] = useState<string | null>(null);
  const [editingService, setEditingService] = useState<Partial<Service>>({});
  const updateServiceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const servicesApp = miniApps.find(app => app.id === 'services');

  useEffect(() => {
    if (!isChatbotLoading && chatbotPublicId) {
      fetchServices();
    }
  }, [chatbotPublicId, isChatbotLoading]);

  const fetchServices = async () => {
    if (!chatbotPublicId) return;
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('services')
        .select('*')
        .eq('chatbot_public_id', chatbotPublicId)
        .order('position');

      if (error) throw error;
      setServices(data || []);
    } catch (err) {
      console.error('Error fetching services:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (serviceId: string, file: File) => {
    if (!chatbotPublicId) return;
    try {
      setUploadingImage(serviceId);
      
      const fileExt = file.name.split('.').pop();
      const fileName = `${serviceId}-${Date.now()}.${fileExt}`;
      const filePath = `services/${chatbotPublicId}/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('chatbot-assets')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('chatbot-assets')
        .getPublicUrl(filePath);

      await handleUpdateService(serviceId, { image_url: publicUrl });
    } catch (err) {
      console.error('Error uploading image:', err);
    } finally {
      setUploadingImage(null);
    }
  };

  const handleAddService = async () => {
    if (!chatbotPublicId) return;
    try {
      const newPosition = services.length;
      const { data, error } = await supabase
        .from('services')
        .insert({
          chatbot_public_id: chatbotPublicId,
          title: 'New service',
          subtitle: 'Subtitles',
          content: 'Service description...',
          image_url: '',
          position: newPosition
        })
        .select()
        .single();

      if (error) throw error;
      
      if (data) {
        const newService = data as Service;
        setServices(prevServices => [...prevServices, newService]);
        setEditingService({ id: newService.id });
      }
    } catch (err) {
      console.error('Error adding service:', err);
    }
  };

  const handleUpdateService = async (id: string, updates: Partial<Service>) => {
    if (!chatbotPublicId) return;
    try {
      const { error } = await supabase
        .from('services')
        .update(updates)
        .eq('id', id)
        .eq('chatbot_public_id', chatbotPublicId);

      if (error) throw error;

      setServices(services.map(service => 
        service.id === id ? { ...service, ...updates } : service
      ));
    } catch (err) {
      console.error('Error updating service:', err);
    }
  };

  const handleRemoveService = async (id: string) => {
    if (!chatbotPublicId) return;
    try {
      const { error } = await supabase
        .from('services')
        .delete()
        .eq('id', id)
        .eq('chatbot_public_id', chatbotPublicId);

      if (error) throw error;

      setServices(services.filter(service => service.id !== id));
    } catch (err) {
      console.error('Error removing service:', err);
    }
  };

  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  const handleDragOver = async (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index || !chatbotPublicId) return;

    const newServices = [...services];
    const draggedService = newServices[draggedIndex];
    newServices.splice(draggedIndex, 1);
    newServices.splice(index, 0, draggedService);

    // Update positions
    const updatedServices = newServices.map((service, i) => ({
      ...service,
      position: i
    }));

    setServices(updatedServices);
    setDraggedIndex(index);

    // Update positions in database
    try {
      // We need to ensure each record has the chatbot_public_id
      const servicesWithChatbotId = updatedServices.map(service => ({
        ...service,
        chatbot_public_id: chatbotPublicId
      }));

      const { error } = await supabase
        .from('services')
        .upsert(servicesWithChatbotId);

      if (error) throw error;
    } catch (err) {
      console.error('Error updating positions:', err);
    }
  };

  const handleInputChange = (
    id: string,
    field: keyof Service,
    value: string,
    debounce = true
  ) => {
    // Update local state immediately
    setEditingService(prev => ({
      ...prev,
      [field]: value
    }));

    // Update the service in the list
    setServices(services.map(service =>
      service.id === id ? { ...service, [field]: value } : service
    ));

    // Debounce the database update
    if (debounce) {
      // Clear previous timeout using the ref
      if (updateServiceTimeoutRef.current) {
        clearTimeout(updateServiceTimeoutRef.current);
      }
      
      // Set new timeout and store it in the ref
      updateServiceTimeoutRef.current = setTimeout(() => {
        handleUpdateService(id, { [field]: value });
        updateServiceTimeoutRef.current = null;
      }, 500);
    } else {
      handleUpdateService(id, { [field]: value });
    }
  };

  if (loading || isChatbotLoading) {
    return (
      <div className="flex justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-indigo-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-white mb-2">Service configuration</h2>
        <p className="text-gray-400">Manage the services displayed in the Services mini-app</p>
      </div>

      {/* Titre de la mini-app */}
      <div className="bg-white/5 backdrop-blur-xl rounded-xl p-6 border border-white/10">
        <h3 className="text-lg font-semibold text-white mb-4">Mini-app title</h3>
        <input
          type="text"
          value={servicesApp?.label || ''}
          onChange={(e) => chatbotPublicId && updateMiniAppLabel('services', e.target.value, chatbotPublicId)}
          className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
          placeholder="Nos services"
        />
      </div>

      <div className="space-y-4">
        {services.map((service, index) => {
          const isEditing = editingService.id === service.id;
          return (
            <div
              key={service.id}
              draggable
              onDragStart={() => handleDragStart(index)}
              onDragOver={(e) => handleDragOver(e, index)}
              onDragEnd={() => setDraggedIndex(null)}
              className="bg-white/5 backdrop-blur-xl rounded-xl p-6 border border-white/10 space-y-4 cursor-move"
            >
              <div className="flex items-center gap-3">
                <GripVertical className="w-5 h-5 text-gray-500" />
                <input
                  type="text"
                  value={isEditing ? (editingService.title || service.title) : service.title}
                  onChange={(e) => handleInputChange(service.id, 'title', e.target.value)}
                  onFocus={() => setEditingService({ id: service.id, title: service.title })}
                  onBlur={() => setEditingService({})}
                  className="flex-1 bg-transparent border-none text-white focus:outline-none text-lg font-medium"
                />
                <button
                  onClick={() => handleRemoveService(service.id)}
                  className="p-1 hover:bg-white/10 rounded"
                >
                  <Trash2 className="w-4 h-4 text-red-400" />
                </button>
                <button 
                  onClick={() => openModal('services')} 
                  className="p-1 hover:bg-white/10 rounded"
                >
                  <Eye className="w-4 h-4 text-gray-400" />
                </button>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Subtitle</label>
                  <input
                    type="text"
                    value={isEditing ? (editingService.subtitle || service.subtitle || '') : (service.subtitle || '')}
                    onChange={(e) => handleInputChange(service.id, 'subtitle', e.target.value)}
                    onFocus={() => setEditingService({ id: service.id, subtitle: service.subtitle })}
                    onBlur={() => setEditingService({})}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                <div className="relative">
                  <label className="block text-sm text-gray-400 mb-1">Image</label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={isEditing ? (editingService.image_url || service.image_url || '') : (service.image_url || '')}
                      onChange={(e) => handleInputChange(service.id, 'image_url', e.target.value)}
                      onFocus={() => setEditingService({ id: service.id, image_url: service.image_url })}
                      onBlur={() => setEditingService({})}
                      className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                    <label className="flex items-center justify-center p-2 bg-white/10 rounded-lg cursor-pointer hover:bg-white/20 transition-colors">
                      <Upload className="w-4 h-4 text-white" />
                      <input
                        type="file"
                        className="hidden"
                        accept="image/*"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) handleImageUpload(service.id, file);
                        }}
                      />
                    </label>
                  </div>
                  {uploadingImage === service.id && (
                    <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                      <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-indigo-500" />
                    </div>
                  )}
                </div>
              </div>
              
              <div>
                <label className="block text-sm text-gray-400 mb-1">Content</label>
                <textarea
                  value={isEditing ? (editingService.content || service.content) : service.content}
                  onChange={(e) => handleInputChange(service.id, 'content', e.target.value)}
                  onFocus={() => setEditingService({ id: service.id, content: service.content })}
                  onBlur={() => setEditingService({})}
                  rows={4}
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Button text</label>
                  <input
                    type="text"
                    value={isEditing ? (editingService.button_text || service.button_text || '') : (service.button_text || '')}
                    onChange={(e) => handleInputChange(service.id, 'button_text', e.target.value)}
                    onFocus={() => setEditingService({ id: service.id, button_text: service.button_text })}
                    onBlur={() => setEditingService({})}
                    placeholder="En savoir plus"
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Button URL</label>
                  <input
                    type="text"
                    value={isEditing ? (editingService.button_url || service.button_url || '') : (service.button_url || '')}
                    onChange={(e) => handleInputChange(service.id, 'button_url', e.target.value)}
                    onFocus={() => setEditingService({ id: service.id, button_url: service.button_url })}
                    onBlur={() => setEditingService({})}
                    placeholder="https://example.com"
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <button
        onClick={handleAddService}
        className="w-full py-3 rounded-lg bg-indigo-500/20 text-indigo-400 font-medium hover:bg-indigo-500/30 transition-colors flex items-center justify-center gap-2"
      >
        <Plus className="w-4 h-4" />
       Add a service
      </button>
    </div>
  );
}